import numeral from 'numeral';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';

// A tiny leftover such as 0.000000002 (from adding up amounts) is written by
// JavaScript as "2e-9", which the formatter showed as "NaN" — treat it as 0.
const tidyNum = (value) => ((typeof value === 'number' && Math.abs(value) < 1e-6) ? 0 : value);

// Currency formatting — reads dynamic symbol AND decimal places from globals
// (set by AuthContext when activeCompany loads). Caller can still override both
// via options.symbol and options.decimals.
export const formatCurrency = (value, options = {}) => {
  const globalDp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__)
    : 2;
  const {
    symbol = (typeof window !== 'undefined' && window.__AMAL_CURRENCY_SYMBOL__) || 'Rs.',
    decimals = globalDp,
    compact = false
  } = options;

  if (value === null || value === undefined) return `${symbol}0`;
  value = tidyNum(value);

  if (compact) {
    if (Math.abs(value) >= 10000000) {
      return `${symbol}${numeral(value / 10000000).format('0.[00]')}Cr`;
    }
    if (Math.abs(value) >= 100000) {
      return `${symbol}${numeral(value / 100000).format('0.[00]')}L`;
    }
    if (Math.abs(value) >= 1000) {
      return `${symbol}${numeral(value / 1000).format('0.[00]')}K`;
    }
  }

  return `${symbol}${numeral(value).format(decimals > 0 ? `0,0.${'0'.repeat(decimals)}` : '0,0')}`;
};

// Number formatting with abbreviation
export const formatNumber = (value, options = {}) => {
  const { decimals = 0, compact = false } = options;

  if (value === null || value === undefined) return '0';
  value = tidyNum(value);

  if (compact) {
    if (Math.abs(value) >= 10000000) {
      return `${numeral(value / 10000000).format('0.[00]')}Cr`;
    }
    if (Math.abs(value) >= 100000) {
      return `${numeral(value / 100000).format('0.[00]')}L`;
    }
    if (Math.abs(value) >= 1000) {
      return `${numeral(value / 1000).format('0.[00]')}K`;
    }
  }

  return numeral(value).format(decimals > 0 ? `0,0.${'0'.repeat(decimals)}` : '0,0');
};

// Company-level date display format.
// Published by AuthContext as window.__AMAL_DATE_FORMAT__ (a date-fns token
// string, e.g. 'dd/MM/yyyy'). Defaults to the original look so any company that
// hasn't chosen a format sees exactly what it saw before. This is DISPLAY ONLY —
// storage and report filters always use the canonical YYYY-MM-DD string.
export const DEFAULT_DATE_FORMAT = 'dd MMM yyyy';
export const getCompanyDateFormat = () =>
  (typeof window !== 'undefined' && window.__AMAL_DATE_FORMAT__) || DEFAULT_DATE_FORMAT;

// Turn any stored date value into a Date object, safely.
// Accepts an ISO string ('2026-07-15' or full ISO datetime), a Firestore
// Timestamp (has .toDate), or a Date. Returns null when it can't.
const toDateObj = (date) => {
  if (!date) return null;
  if (typeof date === 'string') return parseISO(date);
  if (typeof date.toDate === 'function') return date.toDate();
  if (date instanceof Date) return date;
  return null;
};

// Date formatting. When formatStr is omitted, the company's chosen format is
// used; pass an explicit formatStr to force a specific look (that override wins).
export const formatDate = (date, formatStr) => {
  const dateObj = toDateObj(date);
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  return format(dateObj, formatStr || getCompanyDateFormat());
};

// Relative date formatting
export const formatRelativeDate = (date) => {
  const dateObj = toDateObj(date);
  if (!dateObj || isNaN(dateObj.getTime())) return '';

  if (isToday(dateObj)) return 'Today';
  if (isYesterday(dateObj)) return 'Yesterday';

  return formatDistanceToNow(dateObj, { addSuffix: true });
};

// Time formatting
export const formatTime = (date) => {
  const dateObj = toDateObj(date);
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  return format(dateObj, 'HH:mm');
};

// Get greeting based on time of day
export const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

